import { useRouter } from 'next/navigation';
import { ascend, map, pipe, prop, sort } from 'ramda';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { useNavigation } from './navigation';
import { CMSGameTheme } from '@/api/cms/gameThemes';
import { getProviderIcon } from '@/helpers/cdn';
import { RestrictedProviders } from '@/helpers/conf';
import { sortProvidersByAlphabet } from '@/helpers/game';
import { ProviderCustom } from '@/helpers/gameCategories';
import { useCommonStore } from '@/store';

interface GameProvidersProps {
  providerId?: string;
  iconsMode?: 'color' | 'white';
  iconsSize?: 'small' | 'large';
  invertIcons?: boolean;
}

export const useGameProviders = ({
  providerId,
  iconsMode = 'color',
  iconsSize = 'small',
  invertIcons,
}: GameProvidersProps) => {
  const { providers: providerImages } = useTheme();
  const { generateLink } = useNavigation();

  const {
    providers: dataProviders,
    pendingProviders,
    isLiveSpinsAvailable,
  } = useCommonStore(
    useShallow((state) => ({
      providers: state.providers,
      pendingProviders: state.pendingProviders,
      isLiveSpinsAvailable: state.isLiveSpinsAvailable,
    }))
  );

  const providers: ProviderCustom[] = useMemo(
    () =>
      dataProviders
        .filter(
          ({ id }) =>
            (isLiveSpinsAvailable || id !== RestrictedProviders.LIVESPIN) &&
            id !== RestrictedProviders.AUTOPAY
        )
        .map((p) => ({
          ...p,
          pathname: generateLink(`/games/all/${p.id}`),
          icon: getProviderIcon(p.id, iconsMode, iconsSize, invertIcons),
          banner: providerImages[p.id]?.src || providerImages.placeholder,
        })),
    [
      dataProviders,
      invertIcons,
      providerImages,
      iconsSize,
      iconsMode,
      generateLink,
      isLiveSpinsAvailable,
    ]
  );

  const providersSortedByAlphabet = useMemo(
    () => sortProvidersByAlphabet(providers),
    [providers]
  );

  const currentProvider = useMemo(
    () => providers.find(({ id }) => id === providerId),
    [providers, providerId]
  );

  return {
    providers,
    pendingProviders,
    currentProvider,
    providersSortedByAlphabet,
  };
};

export const useGameThemes = (categoryId?: string) => {
  const { generateLink } = useNavigation();
  const gameThemes = useCommonStore.use.gameThemes();

  const formattedGameThemes = useMemo(
    () =>
      pipe(
        sort<CMSGameTheme>(ascend(prop('order'))),
        map((themes) => ({
          id: themes.id,
          categoryId: themes.categoryId,
          title: themes.title,
          icon: themes.icon,
          pathname: generateLink(`/games/${themes.categoryId}#gamesSection`),
          isActive: themes.categoryId === categoryId,
        }))
      )(gameThemes),
    [gameThemes, categoryId, generateLink]
  );

  const currentGameTheme = useMemo(
    () => formattedGameThemes.find((theme) => theme.categoryId === categoryId),
    [formattedGameThemes, categoryId]
  );

  return { gameThemes: formattedGameThemes, currentGameTheme };
};

export const useGameOpenFlow = () => {
  const { push, back } = useRouter();
  const gameOpenBackUrl = useCommonStore.use.gameOpenBackUrl();

  const closeGameFlow = () => {
    gameOpenBackUrl ? push(gameOpenBackUrl) : back();
  };

  return { closeGameFlow };
};
